import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import socket from '../utils/socket';
import { PIE_COLORS } from '../theme/casa';
import '../ui-pages.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// ---- Content (skeleton) loaders — Casa Libre bone shimmer ----------------
const Sk = ({ w = '100%', h = 12, r = 999, style }) => (
  <div className="cl-skel" style={{ width: w, height: h, borderRadius: r, ...style }} />
);
const SkelDonut = () => (
  <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
      <div className="cl-skel" style={{ width: 156, height: 156, borderRadius: '50%' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[0, 1, 2].map(i => <Sk key={i} w={120} h={10} />)}</div>
    </div>
    <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 16 }}>
      {[0, 1, 2, 3].map(i => (<div key={i}><Sk w={140} h={10} style={{ marginBottom: 8 }} /><Sk w="100%" h={8} /></div>))}
    </div>
  </div>
);
const SkelChart = ({ h = 200 }) => <div className="cl-skel" style={{ width: '100%', height: h, borderRadius: 12, flex: 1 }} />;
const SkelBars = ({ rows = 6 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 18, padding: '6px 0', flex: 1 }}>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <Sk w={110} h={12} /><Sk w={`${72 - i * 9}%`} h={8} /><Sk w={40} h={12} />
      </div>
    ))}
  </div>
);
const SkelRows = ({ rows = 5 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 20px' }}>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 0', borderBottom: i === rows - 1 ? 'none' : '1px solid #E7E1D6' }}>
        <div className="cl-skel cl-skel-circle" style={{ width: 38, height: 38 }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}><Sk w="45%" h={11} /><Sk w="80%" h={8} /></div>
        <Sk w={54} h={22} r={999} />
      </div>
    ))}
  </div>
);

// Professional pill dropdown filter (Casa Libre) — replaces wrapping tab rows.
const Dropdown = ({ value, options, onChange, align = 'right' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const cur = options.find(o => o.v === value) || options[0];
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: '#fff', border: `1px solid ${open ? '#111111' : '#E7E1D6'}`, borderRadius: 999, padding: '8px 12px 8px 15px', fontSize: 13, fontWeight: 600, color: '#111111', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'border-color .15s' }}>
        {cur.label}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9C978C" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .18s' }}><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div className="cl-pop" style={{ position: 'absolute', top: 'calc(100% + 7px)', [align]: 0, minWidth: 190, background: '#fff', border: '1px solid #E7E1D6', borderRadius: 14, boxShadow: '4px 4px 0 #111111', padding: 6, zIndex: 120 }}>
          {options.map(o => {
            const on = o.v === value;
            return (
              <button key={o.v} onClick={() => { onChange(o.v); setOpen(false); }}
                onMouseDown={(e) => e.preventDefault()}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%', textAlign: 'left', background: on ? '#111111' : 'transparent', color: on ? '#F9F4EE' : '#3A3A37', border: 'none', borderRadius: 9, padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background .12s' }}
                onMouseEnter={(e) => { if (!on) e.currentTarget.style.background = '#FAF7F1'; }}
                onMouseLeave={(e) => { if (!on) e.currentTarget.style.background = 'transparent'; }}>
                {o.label}
                {on && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Platform meta (labels + accent colors) shared by the charts.
// Categorical dots use the Casa Libre palette in order.
const PLATFORM_META = {
  whatsapp:  { label: 'WhatsApp',  color: PIE_COLORS[0] },
  instagram: { label: 'Instagram', color: PIE_COLORS[1] },
  website:   { label: 'Website',   color: PIE_COLORS[2] },
  facebook:  { label: 'Messenger', color: PIE_COLORS[3] },
};

// Single-series bar lists use one ink colour (Casa Libre).
const INTENT_COLORS = ['#111111', '#111111', '#111111', '#111111', '#111111', '#111111', '#111111', '#111111'];

// Leaderboard bars are single-series: flat ink for every filter.
const PLATFORM_GRAD = {
  all:       '#111111',
  whatsapp:  '#111111',
  instagram: '#111111',
  facebook:  '#111111',
  website:   '#111111',
};

// Lightweight SVG donut (no chart lib). data: [{ value, color }]
const Donut = ({ data, size = 156, stroke = 22 }) => {
  const total = data.reduce((a, d) => a + d.value, 0) || 1;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#FAF7F1" strokeWidth={stroke} />
      {data.filter(d => d.value > 0).map((d, i) => {
        const len = (d.value / total) * c;
        const el = (
          <circle
            key={i} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={d.color} strokeWidth={stroke}
            strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-acc}
            style={{ transition: 'stroke-dasharray .6s ease' }}
          />
        );
        acc += len;
        return el;
      })}
    </svg>
  );
};

// Responsive SVG line chart with a hover tooltip. Uses non-scaling-stroke so
// the line stays crisp under non-uniform scaling; markers are HTML-positioned.
const LineChart = ({ points, color = '#111111', maxLabels = 6 }) => {
  const [hover, setHover] = useState(null);
  const wrapRef = useRef(null);
  const n = points.length;
  const max = Math.max(1, ...points.map(p => p.count));
  const x = (i) => (n <= 1 ? 0 : (i / (n - 1)) * 100);
  const y = (v) => 100 - (v / max) * 86 - 7;
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(2)} ${y(p.count).toFixed(2)}`).join(' ');
  const areaPath = `${linePath} L 100 100 L 0 100 Z`;
  const onMove = (e) => {
    const el = wrapRef.current; if (!el || n === 0) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    setHover(Math.round(ratio * (n - 1)));
  };
  const labelIdxs = [];
  const step = Math.max(1, Math.round((n - 1) / Math.max(1, maxLabels - 1)));
  for (let i = 0; i < n; i += step) labelIdxs.push(i);
  if (labelIdxs[labelIdxs.length - 1] !== n - 1) labelIdxs.push(n - 1);
  const gid = `lf-${color.replace('#', '')}`;

  return (
    <div className="dash-line">
      <div className="dash-line-plot" ref={wrapRef} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.22" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          {[25, 50, 75].map(g => <line key={g} x1="0" x2="100" y1={g} y2={g} stroke="#FAF7F1" strokeWidth="1" vectorEffect="non-scaling-stroke" />)}
          <path d={areaPath} fill={`url(#${gid})`} />
          <path d={linePath} fill="none" stroke={color} strokeWidth="2.25" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
        {hover != null && points[hover] && (
          <>
            <div className="dash-line-vline" style={{ left: `${x(hover)}%` }} />
            <div className="dash-line-dot" style={{ left: `${x(hover)}%`, top: `${y(points[hover].count)}%`, borderColor: color }} />
            <div className="dash-line-tip" style={{ left: `${Math.min(92, Math.max(8, x(hover)))}%` }}>
              <span className="dash-line-tip-v">{points[hover].count} order{points[hover].count === 1 ? '' : 's'}</span>
              <span className="dash-line-tip-l">{points[hover].label}</span>
            </div>
          </>
        )}
      </div>
      <div className="dash-line-x">
        {labelIdxs.map(i => <span key={i} style={{ left: `${x(i)}%` }}>{points[i]?.label}</span>)}
      </div>
    </div>
  );
};

// Vertical bar chart with a dotted-grid backdrop (automation runs).
const BarChart = ({ items }) => {
  const max = Math.max(1, ...items.map(it => it.count));
  return (
    <div className="dash-bars">
      <div className="dash-bars-plot">
        {items.map((it, i) => {
          const c = INTENT_COLORS[i % INTENT_COLORS.length];
          return (
            <div key={i} className="dash-bar-col" title={`${it.name}: ${it.count} runs`}>
              <div className="dash-bar-val">{it.count}</div>
              <div className="dash-bar" style={{ height: `${Math.max(4, (it.count / max) * 86)}%`, background: c }} />
            </div>
          );
        })}
      </div>
      <div className="dash-bars-x">
        {items.map((it, i) => <span key={i} title={it.name}>{it.name}</span>)}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { workspaceUuid } = useParams();
  const [users, setUsers] = useState([]);
  const [online, setOnline] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [sentiment, setSentiment] = useState(null);
  const [presenceStats, setPresenceStats] = useState([]);
  const [activity, setActivity] = useState(null);          // agent-activity payload
  const [actPlatform, setActPlatform] = useState('all');    // chart filter
  const [intents, setIntents] = useState(null);             // top-intents payload
  const [ordersTl, setOrdersTl] = useState(null);           // orders-timeline payload
  const [ordersGran, setOrdersGran] = useState('day');      // 'day' | 'month'
  const [autoRuns, setAutoRuns] = useState(null);           // automation-runs payload

  const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } });

  useEffect(() => {
    if (!workspaceUuid) return;
    let cancelled = false;

    const loadAll = async () => {
      try {
        const [uRes, pRes, sRes, hRes, aRes, iRes, oRes, arRes] = await Promise.all([
          axios.get(`${API_URL}/api/users/workspace-users`, authConfig()),
          axios.get(`${API_URL}/api/users/presence?workspaceUuid=${workspaceUuid}`, authConfig()),
          axios.get(`${API_URL}/api/analytics/sentiment?workspaceUuid=${workspaceUuid}&days=30`, authConfig()).catch(() => null),
          axios.get(`${API_URL}/api/users/presence-stats?workspaceUuid=${workspaceUuid}`, authConfig()).catch(() => null),
          axios.get(`${API_URL}/api/analytics/agent-activity?workspaceUuid=${workspaceUuid}&days=30`, authConfig()).catch(() => null),
          axios.get(`${API_URL}/api/analytics/intents?workspaceUuid=${workspaceUuid}&limit=8`, authConfig()).catch(() => null),
          axios.get(`${API_URL}/api/analytics/orders-timeline?workspaceUuid=${workspaceUuid}`, authConfig()).catch(() => null),
          axios.get(`${API_URL}/api/analytics/automation-runs?workspaceUuid=${workspaceUuid}`, authConfig()).catch(() => null),
        ]);
        if (cancelled) return;
        setUsers(uRes.data || []);
        setOnline(new Set((pRes.data.online || []).map(o => String(o.userId))));
        if (sRes?.data) setSentiment(sRes.data);
        if (hRes?.data?.rows) setPresenceStats(hRes.data.rows);
        if (aRes?.data) setActivity(aRes.data);
        if (iRes?.data) setIntents(iRes.data);
        if (oRes?.data) setOrdersTl(oRes.data);
        if (arRes?.data) setAutoRuns(arRes.data);
      } catch (err) {
        console.error('Dashboard load failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadAll();
    const interval = setInterval(loadAll, 30000);

    const onPresence = (payload) => {
      if (payload?.workspaceUuid !== workspaceUuid) return;
      setOnline(new Set((payload.online || []).map(o => String(o.userId))));
    };
    socket.on('presence:update', onPresence);

    return () => {
      cancelled = true;
      socket.off('presence:update', onPresence);
      clearInterval(interval);
    };
  }, [workspaceUuid]);

  const isOnline = (u) => {
    const ids = [u._id, u.id, u.email, u.name].filter(Boolean).map(String);
    return ids.some(id => online.has(id));
  };

  const onlineCount = users.filter(isOnline).length;

  const statsByUser = new Map(presenceStats.map(s => [String(s.userId), s]));
  const getStatsForUser = (u) => {
    const ids = [u._id, u.id, u.email, u.name].filter(Boolean).map(String);
    for (const id of ids) {
      const hit = statsByUser.get(id);
      if (hit) return hit;
    }
    return null;
  };

  const formatDuration = (ms) => {
    if (!ms || ms < 1000) return '0m';
    const totalMin = Math.floor(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h === 0) return `${m}m`;
    return `${h}h ${m}m`;
  };
  const formatSince = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return dt.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // ----- agent activity chart derivation -----
  const totalMessages = activity?.platformTotals?.all || 0;
  const activeAgents = (activity?.byUser || []).filter(u => u.total > 0).length;

  // ----- KPI summary derivation -----
  const sentTotals = Object.values(sentiment?.byPlatform || {}).reduce((a, s) => {
    a.positive += s.positive || 0; a.neutral += s.neutral || 0; a.negative += s.negative || 0; a.total += s.total || 0;
    return a;
  }, { positive: 0, neutral: 0, negative: 0, total: 0 });
  const positivePct = sentTotals.total ? Math.round((sentTotals.positive / sentTotals.total) * 100) : 0;
  const sentPct = (n) => sentTotals.total ? Math.round((n / sentTotals.total) * 100) : 0;

  const valueFor = (u) => actPlatform === 'all' ? u.total : (u[actPlatform] || 0);
  const chartUsers = (activity?.byUser || [])
    .map(u => ({ ...u, value: valueFor(u) }))
    .filter(u => u.value > 0)
    .sort((a, b) => b.value - a.value);
  const chartMax = Math.max(1, ...chartUsers.map(u => u.value));
  const barGrad = PLATFORM_GRAD[actPlatform] || PLATFORM_GRAD.all;
  const filteredTotal = chartUsers.reduce((a, u) => a + u.value, 0);

  const avatarColor = (str = '') => {
    const palette = ['#111111', '#111111', '#111111', '#111111', '#111111', '#111111', '#111111', '#111111'];
    let h = 0;
    for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) % 9973;
    return palette[h % palette.length];
  };

  const activityTabs = [
    { v: 'all', label: 'All Platforms' },
    { v: 'whatsapp', label: 'WhatsApp' },
    { v: 'instagram', label: 'Instagram' },
    { v: 'facebook', label: 'Messenger' },
    { v: 'website', label: 'Website' },
  ];

  return (
    <div className="ui-page">
      <header className="ui-head">
        <div>
          <h1 className="ui-title">Dashboard</h1>
          <p className="ui-sub">Welcome back — here's what's happening across your workspace.</p>
        </div>
      </header>

      <div className="dash-pair">
      {/* Sentiment — overall donut + per-platform breakdown */}
      <div className="ui-card dash-section" style={{ marginBottom: 16 }}>
        <div className="dash-sec-head">
          <div className="dash-sec-ic" style={{ background: '#FAF7F1', color: '#111111' }}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>
          </div>
          <div style={{ minWidth: 0 }}>
            <h2 className="dash-sec-title">Customer Sentiment</h2>
            <p className="dash-sec-sub">AI-classified on every incoming message over the last {sentiment?.windowDays || 30} days.</p>
          </div>
        </div>

        {!sentiment ? (
          <SkelDonut />
        ) : sentTotals.total === 0 ? (
          <div className="dash-empty">No sentiment data recorded yet.</div>
        ) : (
          <div className="dash-sent">
            <div className="dash-sent-left">
              <div className="dash-donut-wrap">
                <Donut data={[{ value: sentTotals.positive, color: '#3C8C7A' }, { value: sentTotals.neutral, color: '#9C978C' }, { value: sentTotals.negative, color: '#3A3A37' }]} />
                <div className="dash-donut-center">
                  <div className="dash-donut-pct">{sentPct(sentTotals.negative)}%</div>
                  <div className="dash-donut-lbl" style={{ color: '#3A3A37' }}>Negative</div>
                </div>
              </div>
              <div className="dash-sent-legend">
                <span><i style={{ background: '#3C8C7A' }} />Positive <b>{sentPct(sentTotals.positive)}%</b></span>
                <span><i style={{ background: '#9C978C' }} />Neutral <b>{sentPct(sentTotals.neutral)}%</b></span>
                <span><i style={{ background: '#3A3A37' }} />Negative <b>{sentPct(sentTotals.negative)}%</b></span>
              </div>
            </div>

            <div className="dash-sent-list">
              {Object.entries(sentiment.byPlatform).map(([platform, s]) => {
                const total = s.total || 0;
                const pct = (n) => total === 0 ? 0 : Math.round((n / total) * 100);
                const meta = PLATFORM_META[platform] || { label: platform, color: '#9C978C' };
                return (
                  <div key={platform} className="dash-sentrow">
                    <div className="dash-sentrow-head">
                      <span className="dash-sentrow-name"><span className="dash-dot" style={{ background: meta.color }} />{meta.label}</span>
                      <span className="dash-sentrow-total">{total} msg{total === 1 ? '' : 's'}</span>
                    </div>
                    {total === 0 ? (
                      <div className="dash-sentrow-empty">No data yet</div>
                    ) : (
                      <div className="dash-sentrow-bar" title={`Positive ${pct(s.positive)}% · Neutral ${pct(s.neutral)}% · Negative ${pct(s.negative)}%`}>
                        <span style={{ width: `${pct(s.positive)}%`, background: '#3C8C7A' }} />
                        <span style={{ width: `${pct(s.neutral)}%`, background: '#9C978C' }} />
                        <span style={{ width: `${pct(s.negative)}%`, background: '#3A3A37' }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Orders received over time */}
      <div className="ui-card dash-section" style={{ marginBottom: 16 }}>
        <div className="dash-sec-head">
          <div className="dash-sec-ic" style={{ background: '#FAF7F1', color: '#111111' }}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></svg>
          </div>
          <div style={{ minWidth: 0 }}>
            <h2 className="dash-sec-title">Orders Received</h2>
            <p className="dash-sec-sub">{ordersGran === 'day' ? 'Per day over the last 30 days.' : 'Per month over the last 12 months.'}</p>
          </div>
          <div className="dash-sec-right">
            <Dropdown value={ordersGran} options={[{ v: 'day', label: 'Daily' }, { v: 'month', label: 'Monthly' }]} onChange={setOrdersGran} />
          </div>
        </div>
        {!ordersTl ? (
          <SkelChart h={220} />
        ) : (() => {
          const points = ordersGran === 'day' ? (ordersTl.daily || []) : (ordersTl.monthly || []);
          const total = points.reduce((a, p) => a + p.count, 0);
          if (total === 0) return <div className="dash-empty">No orders in this period yet.</div>;
          return <LineChart points={points} color="#111111" maxLabels={4} />;
        })()}
      </div>
      </div>

      <div className="dash-pair">
      {/* Top customer intents (across all chats) */}
      <div className="ui-card dash-section" style={{ marginBottom: 16 }}>
        <div className="dash-sec-head">
          <div className="dash-sec-ic" style={{ background: '#FAF7F1', color: '#111111' }}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>
          </div>
          <div style={{ minWidth: 0 }}>
            <h2 className="dash-sec-title">Top Customer Intents</h2>
            <p className="dash-sec-sub">Most frequent AI-detected intents across all chats{intents?.total ? ` · ${intents.total.toLocaleString()} detections` : ''}.</p>
          </div>
        </div>

        {!intents ? (
          <SkelBars rows={6} />
        ) : (intents.intents || []).length === 0 ? (
          <div className="dash-empty">No intents detected yet. They appear as customers chat in.</div>
        ) : (
          <div className="dash-intents">
            {intents.intents.map((it, i) => {
              const max = Math.max(1, ...intents.intents.map(x => x.count));
              const pct = (it.count / max) * 100;
              const color = INTENT_COLORS[i % INTENT_COLORS.length];
              return (
                <div key={it.intent} className="dash-int-row" style={{ animationDelay: `${Math.min(i * 50, 400)}ms` }}>
                  <div className="dash-int-label" title={it.intent}>{it.intent}</div>
                  <div className="dash-int-track">
                    <div className="dash-int-fill" style={{ width: `${pct}%`, background: color }} />
                  </div>
                  <div className="dash-int-val">{it.count.toLocaleString()}<span className="dash-int-sub">{it.chats} chat{it.chats === 1 ? '' : 's'}</span></div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Automation run counts */}
      <div className="ui-card dash-section" style={{ marginBottom: 16 }}>
        <div className="dash-sec-head">
          <div className="dash-sec-ic" style={{ background: '#FAF7F1', color: '#111111' }}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
          </div>
          <div style={{ minWidth: 0 }}>
            <h2 className="dash-sec-title">Automation Runs</h2>
            <p className="dash-sec-sub">How many times each automation has run — only those triggered more than once.</p>
          </div>
        </div>
        {!autoRuns ? (
          <SkelChart h={220} />
        ) : (autoRuns.runs || []).length === 0 ? (
          <div className="dash-empty">No automation has run more than once yet.</div>
        ) : (
          <BarChart items={autoRuns.runs} />
        )}
      </div>
      </div>

      <div className="dash-bottom">
      {/* Agent activity — leaderboard of who replied, on which platform */}
      <div className="ui-card dash-section" style={{ marginBottom: 16 }}>
        <div className="dash-sec-head">
          <div className="dash-sec-ic" style={{ background: '#FAF7F1', color: '#111111' }}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><rect x="7" y="10" width="3" height="8" rx="1" /><rect x="12" y="6" width="3" height="12" rx="1" /><rect x="17" y="13" width="3" height="5" rx="1" /></svg>
          </div>
          <div style={{ minWidth: 0 }}>
            <h2 className="dash-sec-title">Agent Reply Volume</h2>
            <p className="dash-sec-sub">Human replies sent per agent over the last 30 days.</p>
          </div>
          <div className="dash-sec-right">
            <Dropdown value={actPlatform} options={activityTabs.map(t => ({ v: t.v, label: t.label }))} onChange={setActPlatform} />
          </div>
        </div>

        {loading ? (
          <SkelBars rows={5} />
        ) : chartUsers.length === 0 ? (
          <div className="dash-empty">No agent replies recorded for this filter yet.</div>
        ) : (
          <div className="dash-lb">
            {chartUsers.map((u, i) => {
              const pct = (u.value / chartMax) * 100;
              const share = filteredTotal ? Math.round((u.value / filteredTotal) * 100) : 0;
              return (
                <div key={u.user} className="dash-lb-row" style={{ animationDelay: `${Math.min(i * 50, 400)}ms` }}>
                  <div className="dash-lb-av" style={{ background: avatarColor(u.user) }}>{String(u.user).charAt(0).toUpperCase()}</div>
                  <div className="dash-lb-mid">
                    <div className="dash-lb-line">
                      <span className="dash-lb-name">{u.user}</span>
                      <span className="dash-lb-val">{u.value.toLocaleString()}</span>
                    </div>
                    <div className="dash-lb-line2">
                      <div className="dash-lb-track"><div className="dash-lb-fill" style={{ width: `${pct}%`, background: barGrad }} /></div>
                      <span className="dash-lb-share">{share}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Team presence */}
      <div className="ui-card dash-section" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="dash-tm-head">
          <div className="dash-sec-ic" style={{ background: '#FAF7F1', color: '#111111' }}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
          </div>
          <div style={{ minWidth: 0 }}>
            <h2 className="dash-sec-title">Team Members</h2>
            <p className="dash-sec-sub">Live presence — updates in real time.</p>
          </div>
          <span className="dash-tm-count">{onlineCount} online</span>
        </div>

        {loading ? (
          <SkelRows rows={5} />
        ) : users.length === 0 ? (
          <div className="dash-empty" style={{ padding: 32 }}>No team members yet.</div>
        ) : (
          <div className="dash-tm-list">
            {users
              .slice()
              .sort((a, b) => (isOnline(b) ? 1 : 0) - (isOnline(a) ? 1 : 0))
              .map((u) => {
                const live = isOnline(u);
                const stats = getStatsForUser(u);
                return (
                  <div key={u._id || u.email} className="dash-tm-row">
                    <div className={`dash-tm-av-wrap ${live ? 'on' : ''}`}>
                      <div className="dash-tm-av" style={{ background: avatarColor(u.name || u.email) }}>
                        {String(u.name || u.email || '?').charAt(0).toUpperCase()}
                      </div>
                      <span className={`dash-tm-statusdot ${live ? 'on' : ''}`} />
                    </div>
                    <div className="dash-tm-info">
                      <div className="dash-tm-name">{u.name || u.email}</div>
                      <div className="dash-tm-meta">{u.role || 'Member'}{u.email ? ` · ${u.email}` : ''}</div>
                      <div className="dash-tm-seen">
                        {live
                          ? <>Online since {formatSince(stats?.lastConnectedAt)}</>
                          : stats?.lastDisconnectedAt
                            ? <>Last seen {formatSince(stats.lastDisconnectedAt)}</>
                            : 'Not seen yet'}
                      </div>
                    </div>
                    <div className="dash-tm-right">
                      <div className="dash-tm-pills">
                        <span className="dash-tm-pill"><b>{formatDuration(stats?.todayMs || 0)}</b>today</span>
                        <span className="dash-tm-pill"><b>{formatDuration(stats?.weekMs || 0)}</b>week</span>
                      </div>
                      <span className={`dash-tm-badge ${live ? 'on' : ''}`}>{live ? 'Active' : 'Offline'}</span>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default Dashboard;
